import { useLazyQuery } from "@apollo/client";
import { useCallback, useEffect, useState } from "react";
import { GET_PATIENTS_BY_FILTERING_QUERY } from "../../queries/patientQueries";

export function usePatientListDataFilter(searchCriteria = {}) {
  const [resultsPatientFiltering, setResultsPatientFiltering] = useState([]);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [refreshKey, setRefreshKey] = useState(0);

  const [getPatientsFiltered, { loading, data, error }] = useLazyQuery(
    GET_PATIENTS_BY_FILTERING_QUERY
  );

  const handleSort = column => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  useEffect(() => {
    if (sortColumn) {
      setResultsPatientFiltering(prevResults => {
        if (prevResults.length === 0) return prevResults;

        const sortedResults = [...prevResults].sort((a, b) => {
          const valueA = a[sortColumn];
          const valueB = b[sortColumn];
          const multiplier = sortDirection === "asc" ? 1 : -1;
          return valueA < valueB
            ? -1 * multiplier
            : valueA > valueB
            ? 1 * multiplier
            : 0;
        });
        return sortedResults;
      });
    }
  }, [sortColumn, sortDirection]);

  // Fetch patients when search criteria changes
  useEffect(() => {
    getPatientsFiltered({
      variables: { ...searchCriteria },
      fetchPolicy: "network-only",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(searchCriteria), getPatientsFiltered, refreshKey]);

  // Handle filtered patients data separately
  useEffect(() => {
    if (data && data.patientsByFiltering) {
      const newData = data.patientsByFiltering;
      setResultsPatientFiltering(prevResults => {
        // Compare with previous state
        if (JSON.stringify(newData) !== JSON.stringify(prevResults)) {
          // Apply current sort if one is active
          if (sortColumn) {
            return [...newData].sort((a, b) => {
              const valueA = a[sortColumn];
              const valueB = b[sortColumn];
              const multiplier = sortDirection === "asc" ? 1 : -1;
              return valueA < valueB
                ? -1 * multiplier
                : valueA > valueB
                ? 1 * multiplier
                : 0;
            });
          }
          return newData;
        }
        return prevResults;
      });
    } else if (error) {
      setResultsPatientFiltering([]);
    }
  }, [data, error, sortColumn, sortDirection]);

  const refreshPatients = useCallback(() => {
    setRefreshKey(prevKey => prevKey + 1);
  }, []);

  return {
    loading,
    resultsPatientFiltering,
    error,
    refreshPatients,
    sortColumn,
    sortDirection,
    handleSort,
  };
}
